import { signInWithRedirect, signIn, confirmSignIn } from "aws-amplify/auth";
import { useStore } from "@nanostores/react";
import ReCAPTCHA from "react-google-recaptcha";

import Button from "../Button/Button.tsx";
import GoogleLogo from "./GoogleLogo.tsx";
import Spinner from "../Spinner/Spinner.tsx";
import LinkOut from "./LinkOut.tsx";
import { currentUser } from "../../../store.ts";
import { useEffect, useRef, useState } from "react";
import Input from "./Input.tsx";
import PasswordInput from "./PasswordInput.tsx";
import useAuthErrors, { type ServerError } from "../hooks/useAuthErrors.ts";
import Errors from "./Errors.tsx";
import { trackButtonPress, trackMixpanelEvent } from "../../../lib/mixpanel.ts";
import useFederatedSigninErrors from "../hooks/useFederatedSigninErrors.ts";
import Divider from "./Divider.tsx";
import FormHeader from "./FormHeader.tsx";
import Form from "./Form.tsx";
import {
  clearPostLoginRedirect,
  setEmailInSessionStorage,
  setPostLoginRedirect,
  getEmailFromSessionStorage,
  clearEmailFromSessionStorage,
} from "../../../lib/storage.ts";

interface LoginFormProps {
  successRedirect?: string;
  showHeader?: boolean;
  title?: string;
}

const LoginForm = ({
  successRedirect = "/app/",
  showHeader = false,
  title = "VideoScribe for your browser",
}: LoginFormProps) => {
  const [email, setEmail] = useState(getEmailFromSessionStorage());
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const user = useStore(currentUser);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const errorsRef = useRef<HTMLDivElement>(null);
  const {
    ACTIONS,
    ERRORS,
    errors: authErrors,
    dispatch,
    getErrorsByInput,
    parseAndDispatchServerError,
  } = useAuthErrors();

  let errors = [...authErrors];
  const { error: federatedSignInError, clearFederatedSignInError } =
    useFederatedSigninErrors();
  if (federatedSignInError) {
    errors.push(federatedSignInError);
  }

  useEffect(() => {
    if (user) {
      window.location.href = successRedirect;
    }
  });

  useEffect(() => {
    if (errors.length && errorsRef.current) {
      const targetTop = 160;
      const { top } = errorsRef.current.getBoundingClientRect();
      const offset = top - targetTop;
      window.scrollBy({ top: offset, behavior: "smooth" });
    }
  }, [errors]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleGoogleLogin = async () => {
    if (user) {
      window.location.href = successRedirect;
      return;
    }
    clearFederatedSignInError();
    trackButtonPress("Web Click Login with Google");
    setPostLoginRedirect(successRedirect);

    try {
      await signInWithRedirect({
        provider: "Google",
      });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "UserAlreadyAuthenticatedException") {
          window.location.href = successRedirect;
          return;
        }

        parseAndDispatchServerError(error);
        throw error;
      }
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    setEmailInSessionStorage(value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const validateRequiredFields = () => {
    const emailAction = email ? ACTIONS.REMOVE_ERROR : ACTIONS.ADD_ERROR;
    dispatch({ type: emailAction, payload: ERRORS.EMAIL_REQUIRED_ERROR });

    const passwordAction = password ? ACTIONS.REMOVE_ERROR : ACTIONS.ADD_ERROR;
    dispatch({ type: passwordAction, payload: ERRORS.PASSWORD_REQUIRED_ERROR });

    if (!email || !password) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    recaptchaRef.current?.reset();

    if (loading) return;

    clearFederatedSignInError();
    setLoading(true);

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      dispatch({ type: ACTIONS.ADD_ERROR, payload: ERRORS.RECAPTCHA_ERROR });
      setLoading(false);
      return;
    }

    dispatch({ type: ACTIONS.CLEAR_ERRORS });

    if (!validateRequiredFields()) {
      setLoading(false);
      return;
    }

    try {
      const { nextStep } = await signIn({
        username: email,
        password,
        options: {
          authFlowType: "CUSTOM_WITH_SRP",
          clientMetadata: {
            recaptchaToken,
          },
        },
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
        try {
          await confirmSignIn({
            challengeResponse: recaptchaToken,
          });
        } catch (err) {
          throw err;
        }
      }

      clearEmailFromSessionStorage();
      clearPostLoginRedirect();
      window.location.href = successRedirect;
    } catch (error) {
      let serverError = error as ServerError;
      console.error(serverError);
      trackMixpanelEvent("Web Login Failed", {
        "Failure Reason": serverError.message,
      });
      parseAndDispatchServerError(serverError);
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} variant="bordered">
      {showHeader && (
        <FormHeader arrow>
          <h2>{title}</h2>
        </FormHeader>
      )}

      {errors && errors.length > 0 && (
        <Errors ref={errorsRef} errors={errors} />
      )}

      <div>
        <Button
          dark
          fullWidth
          social
          type="button"
          onClick={handleGoogleLogin}
          Icon={GoogleLogo}
        >
          Login with Google
        </Button>
      </div>

      <Divider>Or</Divider>

      <div>
        <Input
          id="login-email-address"
          label="Email address"
          type="email"
          value={email}
          onChange={handleEmailChange}
          required
          errors={getErrorsByInput(errors, "email")}
        />

        <PasswordInput
          id="login-password"
          label="Password"
          value={password}
          onChange={handlePasswordChange}
          required
          errors={getErrorsByInput(errors, "password")}
        />

        <Button
          fullWidth
          type="submit"
          onClick={() => {
            trackButtonPress("Web Click Login to Continue");
          }}
        >
          {loading ? <Spinner /> : "Login to continue"}
        </Button>

        {mounted && (
          <ReCAPTCHA
            sitekey={import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={recaptchaRef}
          />
        )}
      </div>
      <LinkOut>
        <p>
          <a href="/forgotten-password/">Forgotten your password</a>
        </p>
      </LinkOut>
    </Form>
  );
};

export default LoginForm;
